/**
 * Owner: Masa Bayu
 * File: admin-orders.ts
 * Date:Time: 2026-10-23 09:20
 * Description: §467d-R — klien senarai order pentadbir (`GET /v1/admin/orders`).
 *   Dashboard admin hanya membawa 24 order terkini (`recentOrders`); skrin
 *   "order" perlu senarai PENUH berpenapis + berpaginasi, dan itu laluan baca
 *   yang dibina di hub untuk tujuan ini.
 *
 *   Kontrak yang dikekalkan di sini:
 *     - BACA-SAHAJA: tiada satu pun fungsi menulis; harga tidak boleh ditetapkan
 *       daripada UI (ruling Owner — pentadbir memantau, harga masuk semasa Publish).
 *     - GAGAL-KERAS: respons yang tidak boleh dihurai → ralat dengan mesej hub,
 *       BUKAN senarai kosong. `total` ialah kiraan selepas penapis supaya
 *       "menunjukkan X – Y daripada Z" tidak pernah dikarang.
 *     - Bilangan yang tidak diminta (401/403/5xx) dinaikkan sebagai ralat; 401
 *       juga menandakan sesi tamat melalui `expireHubSession` (§509 F4 — token +
 *       grant hub + muatan artifak setempat dibuang bersama).
 */

import { useEffect, useState } from 'react';
import { getHubAuthToken } from '../publication/hub-auth';
import { expireHubSession, qxpHubOrigin } from '../publication/hub-sync';
import {
  hubErrorMessage,
  parseOrderRows,
  type HubFetch,
  type HubMoney,
  type HubOrderRow,
} from './admin-dashboard';

export interface AdminOrderFilters {
  readonly state?: string | undefined;
  readonly publicationId?: string | undefined;
  readonly accountId?: string | undefined;
  readonly orderId?: string | undefined;
  readonly limit?: number | undefined;
  readonly offset?: number | undefined;
}

export interface AdminOrdersPage {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly returned: number;
  readonly appliedFilters: {
    readonly state: string | null;
    readonly publicationId: string | null;
    readonly accountId: string | null;
    readonly orderId: string | null;
  };
  readonly stateCounts: Readonly<Record<string, number>>;
  readonly items: readonly HubOrderRow[];
  readonly revenueByCurrency: readonly HubMoney[];
}

export interface AdminOrdersState {
  readonly loading: boolean;
  readonly error: string | null;
  readonly page: AdminOrdersPage | null;
  /** Tarik semula tanpa mengubah penapis (butang "Muat semula"). */
  readonly reload: () => void;
}

const EMPTY: AdminOrdersState = {
  loading: false,
  error: null,
  page: null,
  reload: () => undefined,
};

/** Build query string — hanya parameter yang diisi; `undefined` tidak dihantar. */
export function buildOrdersQuery(filters: AdminOrderFilters): string {
  const params = new URLSearchParams();
  const text = (key: string, value: string | undefined): void => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (trimmed.length > 0) params.set(key, trimmed);
  };
  text('state', filters.state);
  text('publicationId', filters.publicationId);
  text('accountId', filters.accountId);
  text('orderId', filters.orderId);
  if (Number.isInteger(filters.limit) && (filters.limit as number) > 0) {
    params.set('limit', String(filters.limit));
  }
  if (Number.isInteger(filters.offset) && (filters.offset as number) >= 0) {
    params.set('offset', String(filters.offset));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

/**
 * `revenueByCurrency` daripada baris HALAMAN ini — per mata wang, tiada jumlah
 * rentas. Baris `paid` yang TIDAK punya mata wang atau nilai (hub menormalkan yang
 * bukan integer/`string` kepada `null`) dilangkau, bukan dinaikkan kepada `USD`
 * atau `0`: hasil yang dilaporkan mesti angka yang pelayan benar-benar hantar.
 */
function revenueOf(items: readonly HubOrderRow[]): readonly HubMoney[] {
  const map = new Map<string, HubMoney>();
  for (const row of items) {
    if (row.state !== 'paid') continue;
    const currency = row.currency;
    const amount = row.amountMinorUnits;
    if (typeof currency !== 'string' || currency.length === 0) continue;
    if (typeof amount !== 'number' || !Number.isInteger(amount)) continue;
    const prev = map.get(currency) ?? { currency, minorUnits: 0, orders: 0 };
    map.set(currency, {
      currency,
      minorUnits: prev.minorUnits + amount,
      orders: prev.orders + 1,
    });
  }
  return [...map.values()];
}

function parsePage(body: unknown): AdminOrdersPage | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const record = body as Record<string, unknown>;
  if (record.ok !== true) return null;
  const items = parseOrderRows(record.items);
  if (items === null) return null;
  const applied =
    record.appliedFilters !== null && typeof record.appliedFilters === 'object'
      ? (record.appliedFilters as Record<string, unknown>)
      : {};
  const counts: Record<string, number> = {};
  if (record.stateCounts !== null && typeof record.stateCounts === 'object') {
    for (const [key, raw] of Object.entries(record.stateCounts as Record<string, unknown>)) {
      if (typeof raw === 'number' && Number.isInteger(raw)) counts[key] = raw;
    }
  }
  const num = (value: unknown): number =>
    typeof value === 'number' && Number.isInteger(value) ? value : 0;
  return {
    total: num(record.total),
    offset: num(record.offset),
    limit: num(record.limit),
    returned: num(record.returned),
    appliedFilters: {
      state: typeof applied.state === 'string' ? applied.state : null,
      publicationId: typeof applied.publicationId === 'string' ? applied.publicationId : null,
      accountId: typeof applied.accountId === 'string' ? applied.accountId : null,
      orderId: typeof applied.orderId === 'string' ? applied.orderId : null,
    },
    stateCounts: counts,
    items,
    revenueByCurrency: revenueOf(items),
  };
}

/** Satu permohonan senarai order; raise `Error(mesej hub)` jika gagal. */
export async function fetchAdminOrders(
  filters: AdminOrderFilters,
  options: { readonly fetchImpl?: HubFetch; readonly origin?: string } = {},
): Promise<AdminOrdersPage> {
  const token = getHubAuthToken();
  if (token === null) throw new Error('Tiada sesi pentadbir');
  const fetchImpl = options.fetchImpl ?? fetch;
  const origin = options.origin ?? qxpHubOrigin();
  const res = await fetchImpl(`${origin}/v1/admin/orders${buildOrdersQuery(filters)}`, {
    method: 'GET',
    headers: { authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    expireHubSession();
    throw new Error('Sesi pentadbir sudah tamat');
  }
  if (!res.ok) throw new Error(await hubErrorMessage(res));
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new Error('Respons order bukan JSON');
  }
  const page = parsePage(body);
  if (page === null) throw new Error('Respons order tidak dapat dihurai');
  return page;
}

/** Kunci kueri supaya dua set penapis tidak bertindih dalam satu render. */
function queryKey(filters: AdminOrderFilters): string {
  return JSON.stringify([
    filters.state ?? '',
    filters.publicationId ?? '',
    filters.accountId ?? '',
    filters.orderId ?? '',
    filters.limit ?? 0,
    filters.offset ?? 0,
  ]);
}

/**
 * Hook senarai order: tarik semula setiap kunci penapis berubah, dan buang
 * keputusan lama bila pengguna menukar penapis ketika permintaan masih berjalan.
 */
export function useAdminOrders(
  filters: AdminOrderFilters,
  options: { readonly fetchImpl?: HubFetch; readonly origin?: string } = {},
): AdminOrdersState {
  const [state, setState] = useState<AdminOrdersState>(EMPTY);
  const [reloadKey, setReloadKey] = useState(0);
  const key = queryKey(filters);

  useEffect(() => {
    let ignore = false;
    setState({ ...EMPTY, loading: true });
    void (async () => {
      try {
        const page = await fetchAdminOrders(filters, options);
        if (!ignore) setState({ ...EMPTY, loading: false, error: null, page });
      } catch (error) {
        if (!ignore) {
          setState({
            ...EMPTY,
            loading: false,
            error: error instanceof Error ? error.message : 'Ralat tidak dikenali',
          });
        }
      }
    })();
    return () => {
      ignore = true;
    };
    // `key` + `reloadKey` ialah pemicu; `filters` dibaca menerusi kunci yang sama.
  }, [key, reloadKey]);

  return { ...state, reload: () => setReloadKey((n) => n + 1) };
}
